import { useState } from "react";
import {
  FileText,
  Plus,
  Trash2,
  ChevronUp,
  ChevronDown,
  ChevronRight,
  Settings,
  Layers,
  Eye,
  EyeOff,
  GripVertical,
  Home,
  Info,
  Calendar,
  Phone,
  FileQuestion,
  Search,
  Map,
} from "lucide-react";
import clsx from "clsx";
import { PropsEditor } from "./PropsEditor";
import { CatalogContent } from "../../api";

interface PagesEditorProps {
  config: any;
  catalog: CatalogContent;
  onChange: (config: any) => void;
  canEdit: boolean;
}

// Icon mapping per page kinds
const PAGE_ICONS: Record<string, React.ElementType> = {
  home: Home,
  about: Info,
  events: Calendar,
  contacts: Phone,
  visit: FileQuestion,
  search: Search,
  mappa: Map,
  default: FileText,
};

export function PagesEditor({
  config,
  catalog,
  onChange,
  canEdit,
}: PagesEditorProps) {
  const [expandedPage, setExpandedPage] = useState<string | null>(null);
  const [showAddPage, setShowAddPage] = useState(false);
  const pages = config?.pages || [];

  const templates = catalog?.templates;

  const updatePages = (newPages: any[]) => {
    onChange({ ...config, pages: newPages });
  };

  const addPage = (template: string) => {
    const newPage = {
      id: `page-${Date.now()}`,
      enabled: true,
      label: "Nuova pagina",
      templateId: template,
      route: {
        pathPattern: template === "home-page" ? "/" : "/" + template,
        pageKind:
          template === "home-page"
            ? "HomePage"
            : template === "map-page"
              ? "MapPage"
              : "ContentPage",
      },
      sections: [],
    };
    updatePages([...pages, newPage]);
    setShowAddPage(false);
    setExpandedPage(newPage.id);
  };

  const updatePage = (pageId: string, updates: any) => {
    const newPages = pages.map((p: any) =>
      p.id === pageId ? { ...p, ...updates } : p,
    );
    updatePages(newPages);
  };

  const removePage = (pageId: string) => {
    if (confirm("Sei sicuro di voler eliminare questa pagina?")) {
      updatePages(pages.filter((p: any) => p.id !== pageId));
    }
  };

  const togglePageEnabled = (pageId: string) => {
    const page = pages.find((p: any) => p.id === pageId);
    if (page) {
      updatePage(pageId, { enabled: !page.enabled });
    }
  };

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h3 className="font-medium text-gray-900">Pagine del sito</h3>
          <p className="text-sm text-gray-500">
            {pages.length} pagine configurate
          </p>
        </div>
        {canEdit && (
          <button
            onClick={() => setShowAddPage(true)}
            className="btn-primary text-sm"
          >
            <Plus className="w-4 h-4 mr-1" />
            Aggiungi pagina
          </button>
        )}
      </div>

      {/* Add page modal */}
      {showAddPage && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">
              Scegli un template
            </h3>
            <div className="space-y-2">
              {Object.entries(templates ?? {}).map(
                ([id, template]: [string, any]) => (
                  <button
                    key={id}
                    onClick={() => addPage(id)}
                    className="w-full p-2 text-left border border-gray-200 rounded-lg hover:border-primary-300 hover:bg-primary-50 transition-colors"
                  >
                    <p className="font-medium text-gray-900">
                      {template.label || id}
                    </p>
                    <p className="text-sm text-gray-500">
                      {template.description || ""}
                    </p>
                  </button>
                ),
              )}
            </div>
            <button
              onClick={() => setShowAddPage(false)}
              className="mt-4 w-full btn-secondary"
            >
              Annulla
            </button>
          </div>
        </div>
      )}

      {/* Pages list */}
      {pages.length === 0 ? (
        <div className="text-center py-8 bg-gray-50 rounded-lg border-2 border-dashed border-gray-300">
          <FileText className="w-10 h-10 mx-auto text-gray-400 mb-2" />
          <p className="text-gray-600">Nessuna pagina configurata</p>
          <p className="text-sm text-gray-500">
            Aggiungi la prima pagina per iniziare
          </p>
        </div>
      ) : (
        <div className="space-y-2">
          {pages.map((page: any, _index: number) => {
            const isExpanded = expandedPage === page.id;
            const PageIcon =
              PAGE_ICONS[page.id] ||
              (page.templateId === "map-page" ? Map : null) ||
              PAGE_ICONS.default;

            return (
              <div
                key={page.id}
                className={clsx(
                  "border rounded-lg overflow-hidden transition-colors",
                  page.enabled
                    ? "border-gray-200"
                    : "border-gray-200 bg-gray-50 opacity-60",
                )}
              >
                {/* Page header */}
                <div className="flex items-center">
                  {canEdit && (
                    <div className="px-2 py-3 cursor-move text-gray-400 hover:text-gray-600">
                      <GripVertical className="w-4 h-4" />
                    </div>
                  )}

                  <button
                    onClick={() => setExpandedPage(isExpanded ? null : page.id)}
                    className="flex-1 px-3 py-3 flex items-center justify-between hover:bg-gray-50 transition-colors"
                  >
                    <div className="flex items-center space-x-3">
                      <PageIcon className="w-5 h-5 text-gray-600" />
                      <div className="text-left">
                        <p className="font-medium text-gray-900">
                          {page.label || page.id}
                        </p>
                        <p className="text-xs text-gray-500 font-mono">
                          {page.route?.pathPattern || "/"}
                        </p>
                      </div>
                    </div>
                    <div className="flex items-center space-x-2">
                      <span className="text-xs text-gray-500">
                        {page.sections?.length || 0} sezioni
                      </span>
                      {isExpanded ? (
                        <ChevronDown className="w-4 h-4 text-gray-400" />
                      ) : (
                        <ChevronRight className="w-4 h-4 text-gray-400" />
                      )}
                    </div>
                  </button>

                  {canEdit && (
                    <div className="flex items-center px-2 space-x-1">
                      <button
                        onClick={() => togglePageEnabled(page.id)}
                        className={clsx(
                          "p-1.5 rounded transition-colors",
                          page.enabled
                            ? "text-green-600 hover:bg-green-50"
                            : "text-gray-400 hover:bg-gray-100",
                        )}
                        title={page.enabled ? "Disabilita" : "Abilita"}
                      >
                        {page.enabled ? (
                          <Eye className="w-4 h-4" />
                        ) : (
                          <EyeOff className="w-4 h-4" />
                        )}
                      </button>
                      <button
                        onClick={() => removePage(page.id)}
                        className="p-1.5 text-red-500 hover:bg-red-50 rounded transition-colors"
                        title="Elimina"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  )}
                </div>

                {/* Expanded content */}
                {isExpanded && (
                  <div className="border-t border-gray-200 p-4 bg-white">
                    <PageDetailEditor
                      page={page}
                      onUpdate={(updates) => updatePage(page.id, updates)}
                      canEdit={canEdit}
                      catalog={catalog}
                    />
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

// Page detail editor component
function PageDetailEditor({
  page,
  onUpdate,
  canEdit,
  catalog,
}: {
  page: any;
  onUpdate: (updates: any) => void;
  canEdit: boolean;
  catalog: CatalogContent;
}) {
  const [activeTab, setActiveTab] = useState<"settings" | "sections">(
    "settings",
  );

  // const allPageKinds = catalog.allowedPageKinds;

  return (
    <div>
      {/* Tabs */}
      <div className="flex space-x-4 mb-4 border-b border-gray-200">
        <button
          onClick={() => setActiveTab("settings")}
          className={clsx(
            "pb-2 text-sm font-medium transition-colors",
            activeTab === "settings"
              ? "text-primary-600 border-b-2 border-primary-600"
              : "text-gray-600 hover:text-gray-900",
          )}
        >
          <Settings className="w-4 h-4 inline mr-1" />
          Impostazioni
        </button>
        <button
          onClick={() => setActiveTab("sections")}
          className={clsx(
            "pb-2 text-sm font-medium transition-colors",
            activeTab === "sections"
              ? "text-primary-600 border-b-2 border-primary-600"
              : "text-gray-600 hover:text-gray-900",
          )}
        >
          <Layers className="w-4 h-4 inline mr-1" />
          Sezioni ({page.sections?.length || 0})
        </button>
      </div>

      {/* Tab content */}
      {activeTab === "settings" && (
        <div className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Label (nome visualizzato)
              </label>
              <input
                type="text"
                value={page.label || ""}
                onChange={(e) => onUpdate({ label: e.target.value })}
                placeholder="Es. Chi siamo"
                disabled={!canEdit}
                className="input"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                ID pagina
              </label>
              <input
                type="text"
                value={page.id || ""}
                disabled
                className="input bg-gray-50 font-mono text-sm"
              />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Path URL
              </label>
              <input
                type="text"
                value={page.route?.pathPattern || ""}
                onChange={(e) =>
                  onUpdate({
                    route: { ...page.route, pathPattern: e.target.value },
                  })
                }
                placeholder="/chi-siamo"
                disabled={!canEdit}
                className="input font-mono"
              />
            </div>
            <div className="block text-sm font-medium text-gray-700 gap-3">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Template
                </label>

                <select
                  value={page.templateId || "content-page"}
                  onChange={(e) => onUpdate({ templateId: e.target.value })}
                  disabled={!canEdit}
                  className="input"
                >
                  {/* Template a seconda del PageKind presi dal catalogo da allowedPageKinds??  */}
                  <option value="home-page">Homepage</option>
                  <option value="content-page">Pagina contenuto</option>
                  <option value="work-details-page">Dettaglio opera</option>
                  <option value="map-page">Pagina Mappa</option>
                  <option value="legal-page">Pagina legale</option>
                  <option value="search-results-page">Risultati Ricerca</option>
                  <option value="collection-page">Pagina Collezione</option>
                  <option value="landing-page">
                    Pagina promozionale per eventi e mostre
                  </option>
                </select>
              </div>
            </div>
          </div>

          {/* <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Page Kind
            </label>
            <select
              value={page.route?.pageKind || "ContentPage"}
              onChange={(e) =>
                onUpdate({
                  route: { ...page.route, pageKind: e.target.value },
                })
              }
              disabled={!canEdit}
              className="input"
            >
              {allPageKinds.map((pageKind) => (
                <option value={pageKind}>{pageKind}</option>
              ))}
            </select>
          </div> */}
        </div>
      )}
      {activeTab === "sections" && (
        <SectionsEditor
          sections={page.sections || []}
          catalog={catalog}
          onUpdate={(sections) => onUpdate({ sections })}
          canEdit={canEdit}
          templateId={page.templateId}
        />
      )}
    </div>
  );
}

// Sections editor for a page
function SectionsEditor({
  sections,
  onUpdate,
  canEdit,
  templateId,
  catalog,
}: {
  sections: any[];
  onUpdate: (sections: any[]) => void;
  canEdit: boolean;
  templateId: string;
  catalog: CatalogContent;
}) {
  const [showAddSection, setShowAddSection] = useState(false);

  type Variant = { id: string; label: string };

  type AvailableSection = {
    id: string;
    label: string;
    slots: string[];
    variants: Variant[];
  };

  const template = catalog?.templates[templateId];

  const availableSections: AvailableSection[] =
    template?.recommended?.map((sectionId: string) => {
      const section = catalog.sections[sectionId];

      return {
        id: sectionId,
        label: section?.label || sectionId,
        slots: section?.allowedSlots || [],
        variants: (section?.variants ?? []).map((v) =>
          typeof v === "string" ? { id: v, label: v } : v,
        ),
      };
    }) || [];

  const addSection = (sectionDef: (typeof availableSections)[0]) => {
    const newSection = {
      id: `${sectionDef.id}-${Date.now()}`,
      enabled: true,
      sectionId: sectionDef.id,
      variant: sectionDef.variants[0].id,
      slot: sectionDef.slots[0],
      order: sections.length,
      props: {},
    };
    onUpdate([...sections, newSection]);
    setShowAddSection(false);
  };

  const updateSection = (sectionId: string, updates: any) => {
    const newSections = sections.map((s) =>
      s.id === sectionId ? { ...s, ...updates } : s,
    );
    onUpdate(newSections);
  };

  const removeSection = (sectionId: string) => {
    onUpdate(sections.filter((s) => s.id !== sectionId));
  };

  const moveSection = (sectionId: string, direction: "up" | "down") => {
    const section = sections.find((s) => s.id === sectionId);
    if (!section) return;

    const sameSlotSections = sections
      .filter((s) => s.slot === section.slot)
      .sort((a, b) => (a.order ?? 0) - (b.order ?? 0));

    const index = sameSlotSections.findIndex((s) => s.id === sectionId);

    const targetIndex = direction === "up" ? index - 1 : index + 1;

    if (targetIndex < 0 || targetIndex >= sameSlotSections.length) return;

    const swapped = [...sameSlotSections];
    [swapped[index], swapped[targetIndex]] = [
      swapped[targetIndex],
      swapped[index],
    ];

    const updatedSlotSections = swapped.map((s, i) => ({
      ...s,
      order: i,
    }));

    // ricostruisce tutte le sections
    const otherSections = sections.filter((s) => s.slot !== section.slot);

    onUpdate([...otherSections, ...updatedSlotSections]);
  };

  // SLOT_ORDER prende direttamente le chiavi degli slot dal catalo
  const SLOT_ORDER: string[] = catalog?.slots ? Object.keys(catalog.slots) : [];

  const SLOT_LABELS: Record<string, string> = catalog?.slots
    ? Object.fromEntries(
        Object.entries(catalog.slots).map(([key, slot]) => [key, slot.label]),
      )
    : {};

  const sectionsBySlot = SLOT_ORDER.map((slot) => {
    const items = sections
      .filter((s) => s.slot === slot)
      .sort((a, b) => (a.order ?? 0) - (b.order ?? 0));

    return {
      slot,
      label: SLOT_LABELS[slot] || slot,
      items,
    };
  }).filter((group) => group.items.length > 0);

  return (
    <div className="space-y-4">
      {/* Add section button */}
      {canEdit && (
        <button
          onClick={() => setShowAddSection(true)}
          className="w-full py-2 border-2 border-dashed border-gray-300 rounded-lg text-gray-600 hover:border-primary-300 hover:text-primary-600 transition-colors flex items-center justify-center"
        >
          <Plus className="w-4 h-4 mr-1" />
          Aggiungi sezione
        </button>
      )}

      {/* Add section modal */}
      {showAddSection && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50">
          <div className="bg-white rounded-lg shadow-xl max-w-lg w-full p-6 max-h-[80vh] overflow-auto">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">
              Aggiungi sezione
            </h3>
            <div className="grid grid-cols-2 gap-2">
              {availableSections.map((section: any) => (
                <button
                  key={section.id}
                  onClick={() => addSection(section)}
                  className="p-3 text-left border border-gray-200 rounded-lg hover:border-primary-300 hover:bg-primary-50 transition-colors"
                >
                  <p className="font-medium text-gray-900 text-sm">
                    {section.label}
                  </p>
                  <p className="text-xs text-gray-500">
                    Slot: {section.slots.join(", ")}
                  </p>
                </button>
              ))}
            </div>
            <button
              onClick={() => setShowAddSection(false)}
              className="mt-4 w-full btn-secondary"
            >
              Annulla
            </button>
          </div>
        </div>
      )}

      {/* Sections grouped by slot */}
      {sections.length === 0 ? (
        <div className="text-center py-6 text-gray-500">
          <Layers className="w-8 h-8 mx-auto mb-2 text-gray-400" />
          <p className="text-sm">Nessuna sezione configurata</p>
        </div>
      ) : (
        <div className="space-y-4">
          {sectionsBySlot.map(({ slot, label, items }) => (
            <div key={slot}>
              <h4 className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">
                Slot: {label}
              </h4>
              <div className="space-y-2">
                {items.map((section) => (
                  <SectionItem
                    key={section.id}
                    section={section}
                    catalog={catalog}
                    onUpdate={(updates) => updateSection(section.id, updates)}
                    onRemove={() => removeSection(section.id)}
                    onMoveUp={() => moveSection(section.id, "up")}
                    onMoveDown={() => moveSection(section.id, "down")}
                    canEdit={canEdit}
                  />
                ))}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// Individual section item
function SectionItem({
  section,
  catalog,
  onUpdate,
  onRemove,
  onMoveUp,
  onMoveDown,
  canEdit,
}: {
  section: any;
  catalog: CatalogContent;
  onUpdate: (updates: any) => void;
  onRemove: () => void;
  onMoveUp: () => void;
  onMoveDown: () => void;
  canEdit: boolean;
}) {
  const [expanded, setExpanded] = useState(false);

  const allSectionInfo = catalog?.sections[section.sectionId];
  const normalizedVariants =
    allSectionInfo?.variants?.map((v: any) =>
      typeof v === "string" ? { id: v, label: v } : v,
    ) ?? [];

  const normalizedSlots = allSectionInfo?.allowedSlots;

  return (
    <div
      className={clsx(
        "border rounded-lg overflow-hidden",
        section.enabled ? "border-gray-200" : "border-gray-200 opacity-50",
      )}
    >
      <div className="flex items-center bg-gray-50">
        <div className="flex flex-col px-1">
          <button
            onClick={onMoveUp}
            className="p-1 text-gray-500 hover:bg-gray-100 rounded transition-colors"
          >
            <ChevronUp className="w-3.5 h-3.5" />
          </button>

          <button
            onClick={onMoveDown}
            className="p-1 text-gray-500 hover:bg-gray-100 rounded transition-colors"
          >
            <ChevronDown className="w-3.5 h-3.5" />
          </button>
        </div>
        <button
          onClick={() => setExpanded(!expanded)}
          className="flex-1 px-3 py-2 flex items-center justify-between hover:bg-gray-100 transition-colors"
        >
          <div className="flex items-center space-x-2">
            <Layers className="w-4 h-4 text-purple-600" />
            <span className="text-sm font-medium text-gray-900">
              {section.sectionId}
            </span>
            {section.variant && (
              <span className="text-xs text-gray-500 bg-gray-200 px-1.5 py-0.5 rounded">
                {section.variant}
              </span>
            )}
          </div>
          {expanded ? (
            <ChevronDown className="w-4 h-4 text-gray-400" />
          ) : (
            <ChevronRight className="w-4 h-4 text-gray-400" />
          )}
        </button>

        {canEdit && (
          <div className="flex items-center px-2 space-x-1">
            <button
              onClick={() => onUpdate({ enabled: !section.enabled })}
              className={clsx(
                "p-1 rounded transition-colors",
                section.enabled
                  ? "text-green-600 hover:bg-green-50"
                  : "text-gray-400 hover:bg-gray-100",
              )}
            >
              {section.enabled ? (
                <Eye className="w-3.5 h-3.5" />
              ) : (
                <EyeOff className="w-3.5 h-3.5" />
              )}
            </button>

            <button
              onClick={onRemove}
              className="p-1 text-red-500 hover:bg-red-50 rounded transition-colors"
            >
              <Trash2 className="w-3.5 h-3.5" />
            </button>
          </div>
        )}
      </div>

      {expanded && (
        <div className="p-3 border-t border-gray-200 bg-white space-y-3">
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">
                Variante
              </label>
              <select
                value={section.variant}
                onChange={(e) => onUpdate({ variant: e.target.value })}
                disabled={!canEdit}
                className="input text-sm"
              >
                {normalizedVariants.map((v) => (
                  <option value={v.id}>{v.label}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">
                Slot
              </label>
              <select
                value={section.slot}
                onChange={(e) => onUpdate({ slot: e.target.value })}
                disabled={!canEdit}
                className="input text-sm"
              >
                {normalizedSlots.map((v) => (
                  <option value={v}>{v}</option>
                ))}
              </select>
            </div>
          </div>

          {/* Props Editor dinamico */}
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-2">
              Proprietà
            </label>
            <PropsEditor
              sectionId={section.sectionId}
              variant={section.variant}
              props={section.props || {}}
              onChange={(props) => onUpdate({ props })}
              canEdit={canEdit}
              bindingTypes={{}}
              catalog={catalog}
            />
          </div>
        </div>
      )}
    </div>
  );
}
